import { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTutorial } from "@/context/TutorialContext";
import { Button } from "@/components/ui/button";
import { X, ArrowLeft, ArrowRight, Compass, ChartLine } from "@phosphor-icons/react";

const CARD_W = 360;
const CARD_H_APPROX = 200;
const PAD = 12;

function computePosition(rect, placement) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top, left, arrow = null;

  const tryBottom = () => {
    top = rect.bottom + PAD;
    left = rect.left + rect.width / 2 - CARD_W / 2;
    arrow = "top";
  };
  const tryTop = () => {
    top = rect.top - CARD_H_APPROX - PAD;
    left = rect.left + rect.width / 2 - CARD_W / 2;
    arrow = "bottom";
  };
  const tryRight = () => {
    top = rect.top + rect.height / 2 - CARD_H_APPROX / 2;
    left = rect.right + PAD;
    arrow = "left";
  };
  const tryLeft = () => {
    top = rect.top + rect.height / 2 - CARD_H_APPROX / 2;
    left = rect.left - CARD_W - PAD;
    arrow = "right";
  };

  const fns = { top: tryTop, bottom: tryBottom, left: tryLeft, right: tryRight };
  const order = [placement, "bottom", "top", "right", "left"].filter(Boolean);
  for (const p of order) {
    const fn = fns[p];
    if (!fn) continue;
    fn();
    if (top >= 8 && left >= 8 && top + CARD_H_APPROX <= vh - 8 && left + CARD_W <= vw - 8) break;
  }
  // Clamp
  left = Math.max(8, Math.min(left, vw - CARD_W - 8));
  top = Math.max(8, Math.min(top, vh - CARD_H_APPROX - 8));
  return { top, left, arrow };
}

export default function TutorialOverlay() {
  const { active, step, stepIdx, total, next, prev, close } = useTutorial();
  const [rect, setRect] = useState(null);
  const [pos, setPos] = useState({ top: 0, left: 0, arrow: null });

  // Track target element rect
  useLayoutEffect(() => {
    if (!step) { setRect(null); return; }
    if (!step.target || step.placement === "center") { setRect(null); return; }

    let raf;
    const measure = () => {
      const el = document.querySelector(`[data-tour-id="${step.target}"]`);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height, right: r.right, bottom: r.bottom });
        setPos(computePosition(r, step.placement || "bottom"));
      } else {
        setRect(null);
      }
    };
    // Retry a few times to allow route change / mount
    let attempts = 0;
    const tick = () => {
      measure();
      attempts += 1;
      if (attempts < 20) raf = requestAnimationFrame(tick);
    };
    tick();

    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [step, stepIdx]);

  useEffect(() => {
    const onKey = (e) => {
      if (!active) return;
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight" || e.key === "Enter") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, close, next, prev]);

  if (!active || !step) return null;

  const isCenter = !step.target || step.placement === "center" || !rect;
  const isLast = stepIdx === total - 1;
  const isFirst = stepIdx === 0;

  const Icon = active === "navigate" ? Compass : ChartLine;
  const accent = active === "navigate" ? "text-lime-400" : "text-cyan-400";
  const accentBg = active === "navigate" ? "bg-lime-400 text-lime-950 hover:bg-lime-500" : "bg-cyan-400 text-cyan-950 hover:bg-cyan-500";

  return createPortal(
    <div className="fixed inset-0 z-[9999]" data-testid="tutorial-overlay">
      {/* Backdrop with optional spotlight cutout */}
      {isCenter ? (
        <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px]" onClick={close} />
      ) : (
        <svg className="absolute inset-0 w-full h-full pointer-events-auto" onClick={close}>
          <defs>
            <mask id="tour-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              <rect
                x={rect.left - 6}
                y={rect.top - 6}
                width={rect.width + 12}
                height={rect.height + 12}
                rx="6"
                fill="black"
              />
            </mask>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.7)" mask="url(#tour-mask)" />
          {/* Highlight ring */}
          <rect
            x={rect.left - 6}
            y={rect.top - 6}
            width={rect.width + 12}
            height={rect.height + 12}
            rx="6"
            fill="none"
            stroke={active === "navigate" ? "#a3e635" : "#22d3ee"}
            strokeWidth="2"
            style={{ pointerEvents: "none" }}
          />
        </svg>
      )}

      {/* Card */}
      <div
        className="absolute w-[360px] bg-zinc-950 border border-white/15 shadow-2xl"
        style={
          isCenter
            ? { top: "50%", left: "50%", transform: "translate(-50%, -50%)" }
            : { top: pos.top, left: pos.left }
        }
        onClick={(e) => e.stopPropagation()}
        data-testid="tutorial-card"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Icon size={16} weight="bold" className={accent} />
            <span className={`font-mono-data text-[10px] tracking-widest ${accent}`}>
              {active === "navigate" ? "COME NAVIGARE" : "COME ANALIZZARE"}
            </span>
            <span className="font-mono-data text-[10px] text-zinc-500">· {stepIdx + 1}/{total}</span>
          </div>
          <button
            onClick={close}
            className="text-zinc-500 hover:text-zinc-100"
            data-testid="tour-close"
            aria-label="Chiudi tutorial"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          <h3 className="font-display font-black text-lg text-zinc-50 mb-2">{step.title}</h3>
          <p className="text-sm text-zinc-400 leading-relaxed">{step.body}</p>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-white/5">
          <div
            className={active === "navigate" ? "h-full bg-lime-400" : "h-full bg-cyan-400"}
            style={{ width: `${((stepIdx + 1) / total) * 100}%`, transition: "width 200ms" }}
          />
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 p-4 border-t border-white/10">
          <Button
            variant="ghost"
            size="sm"
            onClick={close}
            className="rounded-none text-zinc-400 hover:text-zinc-100 hover:bg-white/5 font-mono-data text-xs"
            data-testid="tour-skip"
          >
            SALTA
          </Button>
          <div className="flex-1" />
          {!isFirst && (
            <Button
              variant="outline"
              size="sm"
              onClick={prev}
              className="rounded-none border-white/20 text-zinc-300 hover:bg-white/5 font-mono-data text-xs"
              data-testid="tour-prev"
            >
              <ArrowLeft size={12} weight="bold" className="mr-1" /> INDIETRO
            </Button>
          )}
          <Button
            size="sm"
            onClick={next}
            className={`rounded-none font-mono-data text-xs font-bold ${accentBg}`}
            data-testid="tour-next"
          >
            {isLast ? "FINE" : "AVANTI"}
            {!isLast && <ArrowRight size={12} weight="bold" className="ml-1" />}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
